import SearchIcon from '@mui/icons-material/Search'
import { SoldDialogShippingAnswers } from '@/components/inventory/dialogs/soldShipping'
import { Add as AddIcon } from '@mui/icons-material'
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormLabel,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Radio,
  RadioGroup,
  Step,
  StepLabel,
  Stepper,
  TextField,
  Typography,
  Autocomplete,
  MenuItem,
  Select,
  InputLabel,
  Switch,
  FormGroup,
  Checkbox,
} from '@mui/material'
import { BankProfile, PrivateUserInfoType, SalePaymentType } from '@otw/models'
import { useQueryClient } from '@tanstack/react-query'
import React, { useEffect, useMemo, useState, useRef } from 'react'
import NumberInput from 'components/NumberInput'
import { UserSelector } from 'components/UserSelector'
import states from 'states-us'
import { salesReps } from '@/constants'
import { useBankProfiles } from '@/hooks/admin/useAdminBankProfiles'
import { BankProfileDialog } from '@/views/SuperAdmin/Management/InvoiceManagement/components/BankProfileDialog'
import { useContacts } from '@/hooks/contacts/useContacts'

interface SoldDialogProps {
  open: boolean
  onClose: () => void
  onSubmit: (
    soldToUser: { id: string; name: string },
    soldByUser: { id: string; name: string },
    sold_location: string,
    warrantyInfo: {
      warrantyTerms: string | null
      warrantyCost: number | null
      customerCost: number | null
      warrantyProfit: number | null
    } | null,
    financeInfo: {
      paymentType: SalePaymentType
      downPayment: number | null
      bankName: string | null
      amountFinanced: number | null
      points: number | null
      profit: number | null
      totalSalePrice: number | null
      taxRate: number | null
      includeTaxInSale: boolean
      includeWarrantyInSale: boolean
      includeShippingInSale: boolean
      transleaseLease: boolean
    } | null,
    shippingInfo?: SoldDialogShippingAnswers | null,
    funded?: boolean,
    /** CRM contacts doc id for the buyer (preferred CRM sold-truck key). */
    soldToContactId?: string | null,
  ) => void | boolean | Promise<void | boolean>
  itemName?: string
  actionType?: 'sold' | 'sold_elsewhere' | 'pending_sale' | 'active_deal' | 'pending_available'
  price?: number
  currentLocation?: string
  initialData?: {
    soldToUser?: { id: string; name: string }
    soldToContactId?: string | null
    soldByUser?: { id: string; name: string }
    soldLocation?: string
    warranty?: {
      warrantyTerms: string | null
      warrantyCost: number | null
      customerCost: number | null
      warrantyProfit: number | null
    }
    finance?: {
      paymentType: SalePaymentType | null
      downPayment: number | null
      bankName: string | null
      amountFinanced: number | null
      points: number | null
      profit: number | null
      totalSalePrice: number | null
      taxRate: number | null
      includeTaxInSale: boolean
      includeWarrantyInSale: boolean
      includeShippingInSale: boolean
      transleaseLease?: boolean
    }
    shipping?: SoldDialogShippingAnswers
    /** Whether the deal is already marked funded on the vehicle */
    funded?: boolean
    /** When set, the deal is treated as funded (e.g. pre-marked before sold) so the toggle matches inventory */
    fundedDate?: string | null
  }
}

type SoldDialogInitialData = SoldDialogProps['initialData']

// Define a type for sales rep that matches the structure in constants
interface SalesRep {
  id: string | number
  first_name: string
  last_name: string
}

const DEFAULT_TAX_RATE = 8.25

/** Initial "funded" toggle: true if already funded or a funded date exists on the vehicle. */
function getInitialFundedValue(
  d?: {
    funded?: boolean
    fundedDate?: string | null
  }
): boolean {
  if (!d) return false
  return Boolean(d.funded) || Boolean(d.fundedDate)
}

function buildWarrantyState(d?: SoldDialogInitialData) {
  return {
    warrantyTerms: d?.warranty?.warrantyTerms ?? null,
    warrantyCost: d?.warranty?.warrantyCost ?? null,
    customerCost: d?.warranty?.customerCost ?? null,
    warrantyProfit: d?.warranty?.warrantyProfit ?? 0,
  }
}

function buildFinanceState(d?: SoldDialogInitialData) {
  return {
    paymentType: d?.finance?.paymentType || 'cash',
    downPayment: d?.finance?.downPayment ?? null,
    bankName: d?.finance?.bankName ?? null,
    amountFinanced: d?.finance?.amountFinanced ?? null,
    points: d?.finance?.points ?? null,
    profit: d?.finance?.profit ?? 0,
    totalSalePrice: d?.finance?.totalSalePrice ?? 0,
    taxRate: d?.finance?.taxRate ?? null,
    includeTaxInSale: d?.finance?.includeTaxInSale ?? false,
    includeWarrantyInSale: d?.finance?.includeWarrantyInSale ?? false,
    includeShippingInSale: d?.finance?.includeShippingInSale ?? false,
    transleaseLease: d?.finance?.transleaseLease ?? false,
  }
}

function buildShippingState(d?: SoldDialogInitialData) {
  return {
    shipped: d?.shipping?.shipped ?? null,
    staged: d?.shipping?.staged ?? null,
    notShippedReason: d?.shipping?.notShippedReason ?? '',
    ourShippingCost: d?.shipping?.ourShippingCost ?? null,
    customerShippingCost: d?.shipping?.customerShippingCost ?? null,
    profit: d?.shipping?.profit ?? 0,
  }
}

function buildSaleCalculationState(d?: SoldDialogInitialData) {
  return {
    includeWarranty: d?.finance?.includeWarrantyInSale ?? false,
    includeShipping: d?.finance?.includeShippingInSale ?? false,
    includeTaxes: d?.finance?.includeTaxInSale ?? false,
    taxRate: d?.finance?.taxRate ?? DEFAULT_TAX_RATE,
  }
}

function buildClientFromInitial(d?: SoldDialogInitialData): PrivateUserInfoType | null {
  if (!d?.soldToUser) return null
  return {
    id: d.soldToUser.id,
    first_name: d.soldToUser.name.split(' ')[0] || '',
    last_name: d.soldToUser.name.split(' ').slice(1).join(' ') || '',
    email: '',
  } as PrivateUserInfoType
}

function buildSalesRepState(d?: SoldDialogInitialData): {
  salesRepType: 'house' | 'representative'
  selectedSalesRep: SalesRep | null
  showSalesRepList: boolean
} {
  if (!d?.soldByUser) {
    return { salesRepType: 'house', selectedSalesRep: null, showSalesRepList: false }
  }
  if (d.soldByUser.id === 'house' || d.soldByUser.name === 'House') {
    return { salesRepType: 'house', selectedSalesRep: null, showSalesRepList: false }
  }
  const repName = d.soldByUser.name.split(' ')
  const firstName = repName[0] || ''
  const lastName = repName.slice(1).join(' ') || ''
  const foundRep = salesReps.find(rep => rep.first_name === firstName && rep.last_name === lastName) ?? null
  return {
    salesRepType: 'representative',
    selectedSalesRep: foundRep,
    showSalesRepList: true,
  }
}

// Define step constants - these are indices
const STEP_CLIENT = 0
const STEP_SALES_REP = 1
const STEP_WARRANTY = 2
const STEP_SHIPPING = 3
const STEP_LOCATION = 4
const STEP_FINANCE = 5
const STEP_FUNDED = 6

const WARRANTY_TERM_6_MONTH = '6_month_12_k'
const WARRANTY_TERM_3_YEAR = '3_year_100_k'
const WARRANTY_TERM_5_YEAR = '5_year_75_k'

const TERM_LIST = [
  {
    label: '6 Months/12K',
    value: WARRANTY_TERM_6_MONTH,
  },
  {
    label: '3 Years/100K',
    value: WARRANTY_TERM_3_YEAR,
  },
  {
    label: '5 Years/75K',
    value: WARRANTY_TERM_5_YEAR,
  },
]

const SoldDialog: React.FC<SoldDialogProps> = ({
  open,
  onClose,
  onSubmit,
  itemName,
  actionType = 'sold',
  price,
  currentLocation,
  initialData,
}) => {
  const [salesRepSearchQuery, setSalesRepSearchQuery] = useState('')
  const queryClient = useQueryClient()
  const { contacts: crmContacts } = useContacts()
  const [selectedClient, setSelectedClient] = useState<PrivateUserInfoType | null>(null)
  const [selectedSalesRep, setSelectedSalesRep] = useState<SalesRep | null>(null)
  const [activeStep, setActiveStep] = useState(STEP_CLIENT)
  const hasInitializedStep = useRef(false)
  const hasSyncedOnOpen = useRef(false)
  const initialActionType = useRef<typeof actionType | null>(null)
  const [salesRepType, setSalesRepType] = useState<'house' | 'representative'>('house')
  const [showSalesRepList, setShowSalesRepList] = useState(false)
  const [location, setLocation] = useState('')
  const [warrantyPurchased, setWarrantyPurchased] = useState(false)
  const [warrantyInfo, setWarrantyInfo] = useState(buildWarrantyState())
  const [financeInfo, setFinanceInfo] = useState(buildFinanceState())
  const [shippingInfo, setShippingInfo] = useState(buildShippingState())
  const [funded, setFunded] = useState(false)
  const [selectedBankProfile, setSelectedBankProfile] = useState<BankProfile | null>(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  const { bankProfiles, createBankProfile } = useBankProfiles()
  const [bankProfileDialogOpen, setBankProfileDialogOpen] = useState(false)

  const [saleCalculation, setSaleCalculation] = useState(buildSaleCalculationState())

  const applyInitialData = (d?: SoldDialogInitialData) => {
    setSelectedClient(buildClientFromInitial(d))
    const repState = buildSalesRepState(d)
    setSalesRepType(repState.salesRepType)
    setSelectedSalesRep(repState.selectedSalesRep)
    setShowSalesRepList(repState.showSalesRepList)
    setSalesRepSearchQuery('')
    setLocation(d?.soldLocation || '')
    setWarrantyPurchased(!!d?.warranty)
    setWarrantyInfo(buildWarrantyState(d))
    setFinanceInfo(buildFinanceState(d))
    setShippingInfo(buildShippingState(d))
    setFunded(getInitialFundedValue(d))
    setSaleCalculation(buildSaleCalculationState(d))
    setSelectedBankProfile(null)
  }

  // Sync all vehicle-specific state when the dialog opens; avoids leftover state from a previous always-mounted open
  useEffect(() => {
    if (!open) {
      hasSyncedOnOpen.current = false
      return
    }
    if (hasSyncedOnOpen.current) return
    hasSyncedOnOpen.current = true
    applyInitialData(initialData)
  }, [open, initialData])

  // Sync selectedBankProfile when bankProfiles load (async) or bank name changes
  useEffect(() => {
    if (!open) return
    const bankName = initialData?.finance?.bankName
    if (!bankName || bankProfiles.length === 0) {
      setSelectedBankProfile(null)
      return
    }
    const match =
      bankProfiles.find(p => p.bankName?.toLowerCase() === bankName.toLowerCase()) ?? null
    setSelectedBankProfile(match)
  }, [open, initialData?.finance?.bankName, bankProfiles])

  // Calculate the total sale price based on selections
  const calculateTotalSalePrice = useMemo(() => {
    // Start with base price
    let total = price || 0

    // Add warranty if selected
    if (saleCalculation.includeWarranty && warrantyPurchased && warrantyInfo.customerCost) {
      total += Number(warrantyInfo.customerCost)
    }

    // Add shipping if selected
    if (saleCalculation.includeShipping && shippingInfo.shipped && shippingInfo.customerShippingCost) {
      total += Number(shippingInfo.customerShippingCost)
    }

    // Add taxes if selected
    if (saleCalculation.includeTaxes) {
      total += total * (saleCalculation.taxRate / 100)
    }

    return total
  }, [price, saleCalculation, warrantyInfo, shippingInfo])

  // Update finance total sale price when calculation changes
  useEffect(() => {
    // Update the finance info with the calculated total
    setFinanceInfo(prev => ({
      ...prev,
      totalSalePrice: Number(calculateTotalSalePrice.toFixed(2)),
    }))
  }, [calculateTotalSalePrice])

  // Handler for sale calculation option changes
  const handleSaleCalculationChange = (option: keyof typeof saleCalculation, value: boolean | number) => {
    setSaleCalculation(prev => ({
      ...prev,
      [option]: value,
    }))
  }

  // Store initial actionType when dialog opens to prevent glitching if it changes
  useEffect(() => {
    if (open && initialActionType.current === null) {
      initialActionType.current = actionType
    } else if (!open) {
      initialActionType.current = null
    }
  }, [open, actionType])

  // Use the initial actionType for determining flow, fallback to current if not set
  const effectiveActionType = initialActionType.current ?? actionType

  // Get display text based on effectiveActionType
  const getActionText = () => {
    switch (effectiveActionType) {
      case 'sold_elsewhere':
        return 'Sold Elsewhere'
      case 'pending_sale':
        return 'Pending Sale'
      case 'active_deal':
        return 'Active Deal'
      case 'pending_available':
        return 'Pending Available'
      default:
        return 'Sold'
    }
  }

  const actionText = getActionText()
  // active_deal should show full 6-step dialog like "sold", so exclude it from simplified flow
  const isPendingOnly = effectiveActionType === 'pending_sale' || effectiveActionType === 'pending_available'
  const isActiveDealOrPending =
    effectiveActionType === 'pending_sale' || effectiveActionType === 'active_deal' || effectiveActionType === 'pending_available'

  // Reset submitting state when dialog closes
  useEffect(() => {
    if (!open) {
      setIsSubmitting(false)
    }
  }, [open])

  // Reset all state when dialog is closed or opened
  useEffect(() => {
    if (open && !hasInitializedStep.current) {
      // Check if initialData contains meaningful data that was actually entered by the user
      // (not just default values)
      const hasInitialData = initialData && (
        initialData.soldToUser?.id ||
        initialData.soldByUser?.id ||
        initialData.soldLocation ||
        initialData.warranty?.warrantyTerms ||
        // Only consider finance data meaningful if it's not just default values
        (initialData.finance?.paymentType && initialData.finance.paymentType !== 'cash') ||
        initialData.finance?.downPayment ||
        initialData.finance?.bankName ||
        initialData.finance?.amountFinanced ||
        initialData.finance?.points != null ||
        initialData.finance?.totalSalePrice ||
        initialData.finance?.taxRate ||
        initialData.finance?.includeTaxInSale ||
        initialData.finance?.includeWarrantyInSale ||
        initialData.finance?.includeShippingInSale ||
        initialData.shipping?.shipped === true ||
        initialData.shipping?.shipped === false ||
        initialData.shipping?.ourShippingCost ||
        initialData.shipping?.customerShippingCost
      )

      if (hasInitialData) {
        // If there's already sold data available, we'll skip to the last step
        // For pending states only, that will be the location step
        // For sold and active_deal states, that will be the funded step
        if (isPendingOnly) {
          setActiveStep(STEP_LOCATION)
        } else {
          setActiveStep(STEP_FUNDED)
        }
      } else {
        // Otherwise start at the client step
        setActiveStep(STEP_CLIENT)
      }
      
      hasInitializedStep.current = true
    } else if (!open) {
      // Reset the ref when dialog closes
      hasInitializedStep.current = false
    }
  }, [open, initialData, isPendingOnly])

  // Reset function to clear all state
  const resetDialogState = () => {
    applyInitialData(initialData)
    setActiveStep(STEP_CLIENT)
  }

  // Use salesReps from constants instead of filtering users
  const filteredSalesReps = useMemo(() => {
    if (!salesRepSearchQuery) return salesReps

    const searchLower = salesRepSearchQuery.toLowerCase()
    return salesReps.filter(rep => {
      const fullName = `${rep.first_name} ${rep.last_name}`.toLowerCase()
      return fullName.includes(searchLower)
    })
  }, [salesRepSearchQuery])

  const handleClientSelect = (user: PrivateUserInfoType) => {
    setSelectedClient(user)
    setActiveStep(1) // Move to sales rep step after selecting client
  }

  const handleSalesRepSelect = (rep: SalesRep) => {
    setSelectedSalesRep(rep)
    // For pending sale only, skip the warranty step
    // active_deal should follow full flow like "sold"
    if (isPendingOnly) {
      setActiveStep(STEP_LOCATION) // Move to location step (which is step 2 for pending)
    } else {
      setActiveStep(STEP_WARRANTY) // Move to warranty step for regular sales and active_deal
    }
  }

  const handleSalesRepTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value as 'house' | 'representative'
    setSalesRepType(value)

    if (value === 'house') {
      setSelectedSalesRep(null)
      setShowSalesRepList(false)
    } else {
      setShowSalesRepList(true)
    }
  }

  const handleLocationSelect = (selectedStateAbbr: string) => {
    setLocation(selectedStateAbbr)
  }

  const handleSubmit = async () => {
    if (isSubmitting) return

    // If initialData is present but no client was selected, use the initialData
    const clientData = selectedClient
      ? {
          id: selectedClient.id,
          name: `${selectedClient.first_name} ${selectedClient.last_name}`,
        }
      : initialData?.soldToUser || { id: '', name: '' }

    const soldToContactId =
      (selectedClient
        ? crmContacts?.find((c) => c.userId?.trim() === selectedClient.id)?.id
        : null) ||
      initialData?.soldToContactId?.trim() ||
      null

    // Use "House" as the default if salesRepType is 'house'
    // If no selection was made but we have initialData, use that
    const soldByUser =
      salesRepType === 'representative' && selectedSalesRep
        ? {
            id: String(selectedSalesRep.id),
            name: `${selectedSalesRep.first_name} ${selectedSalesRep.last_name}`,
          }
        : salesRepType === 'house'
          ? {
              id: 'house',
              name: 'House',
            }
          : initialData?.soldByUser || { id: 'house', name: 'House' }

    // If location wasn't changed but we have initialData, use the initial location
    const finalLocation = location || initialData?.soldLocation || ''

    // Only include warranty, finance and shipping info for actual sold items and active_deal, not for pending
    // active_deal should include full info like "sold"
    const warrantyParam = !isPendingOnly && warrantyPurchased ? warrantyInfo : null

    // Set tax rate in finance info when taxes are included in calculation
    const financeParam = !isPendingOnly
      ? {
          ...financeInfo,
          taxRate: saleCalculation.includeTaxes ? saleCalculation.taxRate : null,
          includeTaxInSale: saleCalculation.includeTaxes,
          includeWarrantyInSale: saleCalculation.includeWarranty,
          includeShippingInSale: saleCalculation.includeShipping,
        }
      : null

    const shippingParam = !isPendingOnly
      ? {
          shipped: shippingInfo.shipped,
          staged: shippingInfo.shipped ? null : shippingInfo.staged,
          notShippedReason:
            shippingInfo.shipped === false && shippingInfo.staged === false
              ? shippingInfo.notShippedReason.trim() || null
              : null,
          ourShippingCost: shippingInfo.shipped ? shippingInfo.ourShippingCost : null,
          customerShippingCost: shippingInfo.shipped ? shippingInfo.customerShippingCost : null,
          profit: shippingInfo.shipped ? shippingInfo.profit : null,
        }
      : null

    // Only include funded for sold and active_deal, not for pending
    const fundedParam = !isPendingOnly ? funded : undefined

    setIsSubmitting(true)
    try {
      const result = await onSubmit(
        clientData,
        soldByUser,
        finalLocation,
        warrantyParam,
        financeParam,
        shippingParam,
        fundedParam,
        soldToContactId,
      )
      if (result === false) return
      resetDialogState()
    } finally {
      setIsSubmitting(false)
    }
  }

  // Extended onClose handler
  const handleClose = () => {
    resetDialogState()
    onClose()
  }

  // Add observer for user creation events
  useEffect(() => {
    const handleUserCreated = () => {
      // Use React Query's invalidation instead of a direct refetch
      queryClient.invalidateQueries({ queryKey: ['users'] })
    }

    window.addEventListener('userCreated', handleUserCreated)
    return () => {
      window.removeEventListener('userCreated', handleUserCreated)
    }
  }, [queryClient])

  const handleNext = () => {
    if (activeStep === STEP_SALES_REP && isPendingOnly) {
      // If we're on the sales rep step and it's pending sale only,
      // skip the warranty, shipping, finance steps and go directly to location
      // active_deal should follow full flow like "sold"
      setActiveStep(STEP_LOCATION)
    } else {
      setActiveStep(prevStep => prevStep + 1)
    }
  }

  const handleBack = () => {
    if (activeStep === STEP_LOCATION && isPendingOnly) {
      // If we're on the location step and it's pending sale only,
      // go back to sales rep step (skipping warranty, shipping, finance)
      // active_deal should follow full flow like "sold"
      setActiveStep(STEP_SALES_REP)
    } else {
      setActiveStep(prevStep => prevStep - 1)
    }
  }

  const isNextDisabled = () => {
    if (activeStep === STEP_CLIENT) return !selectedClient
    if (activeStep === STEP_SALES_REP) return salesRepType === 'representative' && !selectedSalesRep
    if (activeStep === STEP_SHIPPING && !isPendingOnly) {
      if (shippingInfo.shipped == null) return true
      if (shippingInfo.shipped === false) {
        if (shippingInfo.staged == null) return true
        if (shippingInfo.staged === false && !shippingInfo.notShippedReason.trim()) return true
      }
    }
    if (activeStep === STEP_LOCATION) return !location
    return false
  }

  const handleChangeWarranty = (key: string, value: any) => {
    const updatedInfo = {
      ...warrantyInfo,
      [key]: value,
    }

    // Auto-calculate warranty profit when warranty costs change
    if (
      (key === 'warrantyCost' || key === 'customerCost') &&
      updatedInfo.warrantyCost !== null &&
      updatedInfo.customerCost !== null
    ) {
      const ourCost = parseFloat(String(updatedInfo.warrantyCost))
      const customerCost = parseFloat(String(updatedInfo.customerCost))
      if (!isNaN(ourCost) && !isNaN(customerCost)) {
        updatedInfo.warrantyProfit = Number((customerCost - ourCost).toFixed(2))
      }
    }

    setWarrantyInfo(updatedInfo)
  }

  const handleSubmitBankProfile = (
    data: Omit<BankProfile, 'id' | 'created_at' | 'updated_at' | 'created_by' | 'updated_by'>
  ) => {
    createBankProfile(
      { data },
      {
        onSuccess: (newProfile: BankProfile) => {
          setSelectedBankProfile(newProfile)
          handleChangeFinance('bankName', newProfile.bankName)
          setBankProfileDialogOpen(false)
        },
      }
    )
  }

  const handleChangeFinance = (key: string, value: any) => {
    const updatedInfo = {
      ...financeInfo,
      [key]: value,
    }

    if (key === 'paymentType' && value === 'cash') {
      updatedInfo.transleaseLease = false
    }

    // Auto-calculate points amount when amount financed, down payment, or points change
    if (
      (key === 'amountFinanced' || key === 'points' || key === 'downPayment') &&
      updatedInfo.amountFinanced &&
      updatedInfo.points != null &&
      updatedInfo.paymentType === 'finance'
    ) {
      const amountFinanced = parseFloat(String(updatedInfo.amountFinanced))
      const downPayment = parseFloat(String(updatedInfo.downPayment || '0'))
      const points = parseFloat(String(updatedInfo.points))

      if (!isNaN(amountFinanced) && !isNaN(points)) {
        // Subtract down payment from amount financed before calculating profit
        const financeAmount = Math.max(0, amountFinanced - downPayment)
        // Convert to number instead of string to match the type
        updatedInfo.profit = Number(((financeAmount * points) / 100).toFixed(2))
      }
    }

    setFinanceInfo(updatedInfo)
  }

  const handleChangeShipping = (key: string, value: boolean | number | string | null) => {
    const updatedInfo = {
      ...shippingInfo,
      [key]: value,
    } as typeof shippingInfo

    if (key === 'shipped' && value === true) {
      updatedInfo.staged = null
      updatedInfo.notShippedReason = ''
    }

    if (key === 'shipped' && value === false) {
      updatedInfo.ourShippingCost = null
      updatedInfo.customerShippingCost = null
      updatedInfo.profit = 0
    }

    if (key === 'staged' && value === true) {
      updatedInfo.notShippedReason = ''
    }

    // Auto-calculate profit when shipping costs change
    if (
      (key === 'ourShippingCost' || key === 'customerShippingCost') &&
      updatedInfo.ourShippingCost !== null &&
      updatedInfo.customerShippingCost !== null
    ) {
      const ourCost = parseFloat(String(updatedInfo.ourShippingCost))
      const customerCost = parseFloat(String(updatedInfo.customerShippingCost))
      if (!isNaN(ourCost) && !isNaN(customerCost)) {
        updatedInfo.profit = Number((customerCost - ourCost).toFixed(2))
      }
    }

    setShippingInfo(updatedInfo)
  }

  // Define the steps based on the action type
  // active_deal should show full 7-step dialog like "sold"
  const steps = isPendingOnly
    ? ['Select Client', 'Select Sales Rep', 'Select Location']
    : ['Select Client', 'Select Sales Rep', 'Fill Warranty', 'Shipping', 'Select Location', 'Financing', 'Funded']

  // Calculate the correct final step index
  const finalStepIndex = steps.length - 1

  return (
    <>
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth='md'
      fullWidth
      keepMounted={false}
      disableRestoreFocus
      PaperProps={{
        sx: {
          height: {
            xs: '95vh', // 95% of viewport height on mobile
            sm: '80vh', // 80% of viewport height on tablet/desktop
          },
          maxHeight: '800px', // Increased maximum height
          width: {
            xs: '95%', // 95% of viewport width on mobile
            sm: '80%', // 80% of viewport width on tablet/desktop
          },
          maxWidth: '900px', // Increased maximum width
        },
      }}
    >
      <DialogTitle>Mark Item as {actionText}</DialogTitle>
      <DialogContent
        sx={{
          display: 'flex',
          flexDirection: 'column',
          height: '100%',
          p: { xs: 1, sm: 2 }, // Responsive padding
        }}
      >
        {itemName && (
          <Typography variant='body1' sx={{ mb: 2 }}>
            Item: {itemName}
          </Typography>
        )}

        <Stepper activeStep={activeStep} sx={{ mb: 3 }}>
          {steps.map(label => (
            <Step key={label}>
              <StepLabel>{label}</StepLabel>
            </Step>
          ))}
        </Stepper>

        {activeStep === STEP_CLIENT && (
          <>
            {(selectedClient || initialData?.soldToUser) && (
              <Box sx={{ mb: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                <Typography variant='subtitle1'>Selected Client:</Typography>
                {selectedClient ? (
                  <>
                    <Typography variant='body1'>
                      {selectedClient.first_name} {selectedClient.last_name}
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                      {selectedClient.email}
                    </Typography>
                  </>
                ) : initialData?.soldToUser ? (
                  <Typography variant='body1'>{initialData.soldToUser.name}</Typography>
                ) : null}
              </Box>
            )}
            <UserSelector
              onUserSelect={handleClientSelect}
              title='Select Client'
              placeholder='Search clients...'
              showCreateButton={true}
            />
          </>
        )}

        {activeStep === STEP_SALES_REP && (
          <>
            <FormControl component='fieldset' sx={{ mb: 2 }}>
              <FormLabel component='legend'>Sales Representative Type</FormLabel>
              <RadioGroup row name='salesRepType' value={salesRepType} onChange={handleSalesRepTypeChange}>
                <FormControlLabel value='house' control={<Radio />} label='House' />
                <FormControlLabel value='representative' control={<Radio />} label='Sales Representative' />
              </RadioGroup>
            </FormControl>

            {salesRepType === 'house' && (
              <Box sx={{ mb: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                <Typography variant='subtitle1'>Selected Sales Rep:</Typography>
                <Typography variant='body1'>House</Typography>
              </Box>
            )}

            {salesRepType === 'representative' && (
              <>
                <TextField
                  autoFocus={salesRepType === 'representative'}
                  margin='dense'
                  fullWidth
                  value={salesRepSearchQuery}
                  onChange={e => setSalesRepSearchQuery(e.target.value)}
                  placeholder='Search sales representatives...'
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position='start'>
                        <SearchIcon />
                      </InputAdornment>
                    ),
                  }}
                  sx={{ mb: 2 }}
                />
                {!selectedSalesRep && !initialData?.soldByUser && salesRepType === 'representative' && (
                  <Typography variant='body2' color='error' sx={{ mb: 2 }}>
                    Please select a sales representative to continue
                  </Typography>
                )}
                {(selectedSalesRep || (initialData?.soldByUser && initialData.soldByUser.id !== 'house')) && (
                  <Box sx={{ mb: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                    <Typography variant='subtitle1'>Selected Sales Rep:</Typography>
                    {selectedSalesRep ? (
                      <Typography variant='body1'>
                        {selectedSalesRep.first_name} {selectedSalesRep.last_name}
                      </Typography>
                    ) : initialData?.soldByUser ? (
                      <Typography variant='body1'>{initialData.soldByUser.name}</Typography>
                    ) : null}
                  </Box>
                )}
                <List
                  sx={{
                    flex: 1,
                    bgcolor: 'background.paper',
                    borderRadius: 1,
                    border: 1,
                    borderColor: 'divider',
                    overflow: 'auto',
                    '& .MuiListItem-root': {
                      height: {
                        xs: '60px', // Smaller height on mobile
                        sm: '72px', // Regular height on tablet/desktop
                      },
                    },
                  }}
                >
                  {filteredSalesReps.length > 0 ? (
                    filteredSalesReps.map(salesRep => (
                      <ListItem
                        key={salesRep.id.toString()}
                        disablePadding
                        sx={{
                          '&:hover': {
                            bgcolor: 'action.hover',
                          },
                        }}
                      >
                        <ListItemButton onClick={() => handleSalesRepSelect(salesRep)}>
                          <ListItemText
                            primary={
                              <Typography variant='body1' noWrap>
                                {salesRep.first_name + ' ' + salesRep.last_name}
                              </Typography>
                            }
                          />
                        </ListItemButton>
                      </ListItem>
                    ))
                  ) : (
                    <ListItem>
                      <ListItemText
                        primary='No sales representatives found'
                        secondary='Try adjusting your search terms'
                      />
                    </ListItem>
                  )}
                </List>
              </>
            )}
          </>
        )}

        {activeStep === STEP_WARRANTY && !isPendingOnly && (
          <>
            <Typography variant='h6' sx={{ mb: 2 }}>
              Warranty Information
            </Typography>
            <FormControlLabel
              control={
                <Switch checked={warrantyPurchased} onChange={evt => setWarrantyPurchased(evt.target.checked)} />
              }
              label='Warranty Purchased'
            />
            {warrantyPurchased && (
              <>
                <FormControl fullWidth>
                  <InputLabel id='demo-simple-select-label'>Terms</InputLabel>
                  <Select
                    labelId='demo-simple-select-label'
                    id='demo-simple-select'
                    value={warrantyInfo?.warrantyTerms}
                    label='Select Option'
                    onChange={evt => handleChangeWarranty('warrantyTerms', evt.target.value)}
                  >
                    {TERM_LIST.map(item => (
                      <MenuItem key={item.value} value={item.value}>
                        {item.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Box display='grid' gridTemplateColumns='1fr 1fr' gap={2} sx={{ marginTop: '10px' }}>
                  <NumberInput
                    label='Warranty Cost'
                    variant='outlined'
                    fullWidth
                    value={warrantyInfo?.warrantyCost}
                    onChange={value => handleChangeWarranty('warrantyCost', value)}
                    decimalScale={2}
                    step={0.01}
                    min={0}
                  />
                  <NumberInput
                    label='Customer Cost'
                    variant='outlined'
                    fullWidth
                    value={warrantyInfo?.customerCost}
                    onChange={value => handleChangeWarranty('customerCost', value)}
                    decimalScale={2}
                    step={0.01}
                    min={0}
                  />
                </Box>

                {warrantyInfo.warrantyCost !== null &&
                  warrantyInfo.customerCost !== null &&
                  warrantyInfo.warrantyCost !== undefined &&
                  warrantyInfo.customerCost !== undefined && (
                    <Box sx={{ mb: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1, mt: 2 }}>
                      <Typography variant='subtitle1'>Warranty Profit:</Typography>
                      <Typography variant='h6' color={warrantyInfo.warrantyProfit < 0 ? 'error' : 'primary'}>
                        $
                        {Number(warrantyInfo.warrantyProfit).toLocaleString('en-US', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </Typography>
                      <Typography variant='body2' color='text.secondary'>
                        (Customer Cost: $
                        {Number(warrantyInfo.customerCost).toLocaleString('en-US', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}{' '}
                        - Our Cost: $
                        {Number(warrantyInfo.warrantyCost).toLocaleString('en-US', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                        )
                      </Typography>
                    </Box>
                  )}
              </>
            )}
          </>
        )}

        {activeStep === STEP_SHIPPING && !isPendingOnly && (
          <>
            <Typography variant='h6' sx={{ mb: 2 }}>
              Shipping Information
            </Typography>
            <FormControl component='fieldset' sx={{ mb: 2 }}>
              <FormLabel component='legend'>Is this being shipped?</FormLabel>
              <RadioGroup
                row
                value={shippingInfo.shipped == null ? '' : String(shippingInfo.shipped)}
                onChange={evt => handleChangeShipping('shipped', evt.target.value === 'true')}
              >
                <FormControlLabel value='true' control={<Radio />} label='Yes' />
                <FormControlLabel value='false' control={<Radio />} label='No' />
              </RadioGroup>
            </FormControl>

            {shippingInfo.shipped === false && (
              <FormControl component='fieldset' sx={{ mb: 2 }}>
                <FormLabel component='legend'>Is this being staged?</FormLabel>
                <RadioGroup
                  row
                  value={shippingInfo.staged == null ? '' : String(shippingInfo.staged)}
                  onChange={evt => handleChangeShipping('staged', evt.target.value === 'true')}
                >
                  <FormControlLabel value='true' control={<Radio />} label='Yes' />
                  <FormControlLabel value='false' control={<Radio />} label='No' />
                </RadioGroup>
              </FormControl>
            )}

            {shippingInfo.shipped === false && shippingInfo.staged === false && (
              <TextField
                label='Why?'
                fullWidth
                multiline
                minRows={2}
                sx={{ mb: 2 }}
                value={shippingInfo.notShippedReason}
                onChange={evt => handleChangeShipping('notShippedReason', evt.target.value)}
              />
            )}

            {shippingInfo.shipped && (
              <>
                <Box display='grid' gridTemplateColumns='1fr 1fr' gap={2} sx={{ mb: 2 }}>
                  <NumberInput
                    label='Our Shipping Cost'
                    fullWidth
                    value={shippingInfo.ourShippingCost}
                    onChange={value => handleChangeShipping('ourShippingCost', value)}
                    decimalScale={2}
                    step={0.01}
                    min={0}
                  />
                  <NumberInput
                    label='Customer Shipping Cost'
                    fullWidth
                    value={shippingInfo.customerShippingCost}
                    onChange={value => handleChangeShipping('customerShippingCost', value)}
                    decimalScale={2}
                    step={0.01}
                    min={0}
                  />
                </Box>

                {(shippingInfo.profit !== 0 ||
                  (shippingInfo.ourShippingCost != null && shippingInfo.customerShippingCost != null)) && (
                  <Box sx={{ mb: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                    <Typography variant='subtitle1'>Shipping Profit:</Typography>
                    <Typography variant='h6' color={shippingInfo.profit < 0 ? 'error' : 'primary'}>
                      $
                      {Number(shippingInfo.profit).toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                      (Customer Cost: $
                      {Number(shippingInfo.customerShippingCost).toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}{' '}
                      - Our Cost: $
                      {Number(shippingInfo.ourShippingCost).toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                      )
                    </Typography>
                  </Box>
                )}
              </>
            )}
          </>
        )}

        {activeStep === STEP_LOCATION && (
          <>
            <Typography variant='h6' sx={{ mb: 2 }}>
              Select Buyer Location
            </Typography>

            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 2,
                mb: 3,
              }}
            >
              {/* Current Vehicle Location */}
              <Box
                sx={{
                  flex: 1,
                  p: 2,
                  bgcolor: 'action.hover',
                  borderRadius: 1,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Typography variant='subtitle2' color='text.secondary'>
                  Current Location:
                </Typography>
                <Typography variant='body1' fontWeight='medium'>
                  {currentLocation && states.find(state => state.abbreviation === currentLocation)
                    ? `${states.find(state => state.abbreviation === currentLocation)?.name} (${currentLocation})`
                    : currentLocation || 'Unknown'}
                </Typography>
              </Box>

              {/* Arrow */}
              <Box sx={{ display: 'flex', alignItems: 'center', px: 2 }}>
                <Typography sx={{ fontSize: '24px' }}>→</Typography>
              </Box>

              {/* Selected Buyer Location */}
              <Box
                sx={{
                  flex: 1,
                  p: 2,
                  bgcolor: location ? 'primary.dark' : 'action.hover',
                  color: location ? 'primary.contrastText' : 'text.primary',
                  borderRadius: 1,
                  display: 'flex',
                  flexDirection: 'column',
                }}
              >
                <Typography variant='subtitle2' color={location ? 'inherit' : 'text.secondary'}>
                  Buyer Location:
                </Typography>
                <Typography variant='body1' fontWeight='medium'>
                  {location
                    ? `${states.find(state => state.abbreviation === location)?.name} (${location})`
                    : initialData?.soldLocation
                      ? `${states.find(state => state.abbreviation === initialData.soldLocation)?.name} (${initialData.soldLocation})`
                      : 'Not selected'}
                </Typography>
              </Box>
            </Box>

            <Autocomplete
              options={states}
              getOptionLabel={option => `${option.name} (${option.abbreviation})`}
              value={states.find(state => state.abbreviation === location) || null}
              onChange={(_, value) => handleLocationSelect(value?.abbreviation || '')}
              isOptionEqualToValue={(option, value) => option.abbreviation === value.abbreviation}
              renderInput={params => (
                <TextField
                  {...params}
                  label='Select buyer location'
                  fullWidth
                  placeholder='Search for a location...'
                />
              )}
              sx={{ mb: 2 }}
            />
          </>
        )}

        {activeStep === STEP_FINANCE && !isPendingOnly && (
          <>
            <Typography variant='h6' sx={{ mb: 2 }}>
              Funding
            </Typography>

            <FormControl fullWidth sx={{ mb: 2 }}>
              <InputLabel id='payment-type-label'>Payment Type</InputLabel>
              <Select
                labelId='payment-type-label'
                value={financeInfo.paymentType}
                onChange={evt => handleChangeFinance('paymentType', evt.target.value)}
                label='Payment Type'
              >
                <MenuItem value='cash'>Cash</MenuItem>
                <MenuItem value='finance'>Finance</MenuItem>
                <MenuItem value='customer_bank'>Customer's Bank</MenuItem>
              </Select>
            </FormControl>

            {financeInfo.paymentType === 'finance' && (
              <>
                <Autocomplete
                  options={bankProfiles}
                  getOptionLabel={(option) =>
                    typeof option === 'string'
                      ? option
                      : option.nickname
                        ? `${option.bankName} (${option.nickname})`
                        : option.bankName
                  }
                  value={
                    selectedBankProfile ??
                    (financeInfo.bankName && !selectedBankProfile
                      ? bankProfiles.find((p) => p.bankName === financeInfo.bankName) ?? financeInfo.bankName
                      : null)
                  }
                  onChange={(_, value) => {
                    if (value && typeof value === 'object' && 'bankName' in value) {
                      setSelectedBankProfile(value as BankProfile)
                      handleChangeFinance('bankName', (value as BankProfile).bankName)
                    } else {
                      setSelectedBankProfile(null)
                      handleChangeFinance('bankName', typeof value === 'string' ? value : '')
                    }
                  }}
                  freeSolo
                  renderInput={(params) => <TextField {...params} label='Bank Name' />}
                  sx={{ mb: 2 }}
                />
                {selectedBankProfile?.overnightAddress && (
                  <Box sx={{ mb: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                    <Typography variant='subtitle2' color='text.secondary' gutterBottom>
                      Address
                    </Typography>
                    <Typography variant='body2'>
                      {[
                        selectedBankProfile.overnightAddress.street,
                        selectedBankProfile.overnightAddress.city,
                        [selectedBankProfile.overnightAddress.state, selectedBankProfile.overnightAddress.zipCode]
                          .filter(Boolean)
                          .join(' '),
                        selectedBankProfile.overnightAddress.country || 'US',
                      ]
                        .filter(Boolean)
                        .join(', ')}
                    </Typography>
                  </Box>
                )}
                <Button
                  startIcon={<AddIcon />}
                  onClick={() => setBankProfileDialogOpen(true)}
                  sx={{ mb: 2 }}
                  size='small'
                >
                  Create new bank profile
                </Button>
                <NumberInput
                  label='Down Payment'
                  fullWidth
                  value={financeInfo.downPayment}
                  onChange={value => handleChangeFinance('downPayment', value)}
                  decimalScale={2}
                  step={0.01}
                  min={0}
                  sx={{ mb: 2 }}
                />
                <Box display='grid' gridTemplateColumns='1fr 1fr' gap={2} sx={{ mb: 2 }}>
                  <NumberInput
                    label='Amount Financed'
                    fullWidth
                    value={financeInfo.amountFinanced}
                    onChange={value => handleChangeFinance('amountFinanced', value)}
                    decimalScale={2}
                    step={0.01}
                    min={0}
                  />
                  <TextField
                    label='Points'
                    type='number'
                    fullWidth
                    value={financeInfo.points ?? ''}
                    onChange={evt => handleChangeFinance('points', evt.target.value)}
                    InputProps={{
                      endAdornment: <InputAdornment position='end'>%</InputAdornment>,
                    }}
                  />
                </Box>

                {financeInfo.amountFinanced && financeInfo.points != null && (
                  <Box sx={{ mb: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                    <Typography variant='subtitle1'>Finance Profit:</Typography>
                    <Typography variant='h6' color='primary'>
                      $
                      {Number(financeInfo.profit).toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </Typography>
                    <Typography variant='body2' color='text.secondary'>
                      ({financeInfo.points}% of $
                      {Number(financeInfo.amountFinanced).toLocaleString('en-US', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                      {financeInfo.downPayment
                        ? ` (after $${Number(financeInfo.downPayment).toLocaleString('en-US', {
                            minimumFractionDigits: 2,
                            maximumFractionDigits: 2,
                          })} down payment)`
                        : ''}
                      )
                    </Typography>
                  </Box>
                )}
              </>
            )}

            {financeInfo.paymentType === 'customer_bank' && (
              <>
                <Autocomplete
                  options={bankProfiles}
                  getOptionLabel={(option) =>
                    typeof option === 'string'
                      ? option
                      : option.nickname
                        ? `${option.bankName} (${option.nickname})`
                        : option.bankName
                  }
                  value={
                    selectedBankProfile ??
                    (financeInfo.bankName && !selectedBankProfile
                      ? bankProfiles.find((p) => p.bankName === financeInfo.bankName) ?? financeInfo.bankName
                      : null)
                  }
                  onChange={(_, value) => {
                    if (value && typeof value === 'object' && 'bankName' in value) {
                      setSelectedBankProfile(value as BankProfile)
                      handleChangeFinance('bankName', (value as BankProfile).bankName)
                    } else {
                      setSelectedBankProfile(null)
                      handleChangeFinance('bankName', typeof value === 'string' ? value : '')
                    }
                  }}
                  freeSolo
                  renderInput={(params) => <TextField {...params} label='Bank Name' />}
                  sx={{ mb: 2 }}
                />
                {selectedBankProfile?.overnightAddress && (
                  <Box sx={{ mb: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                    <Typography variant='subtitle2' color='text.secondary' gutterBottom>
                      Address
                    </Typography>
                    <Typography variant='body2'>
                      {[
                        selectedBankProfile.overnightAddress.street,
                        selectedBankProfile.overnightAddress.city,
                        [selectedBankProfile.overnightAddress.state, selectedBankProfile.overnightAddress.zipCode]
                          .filter(Boolean)
                          .join(' '),
                        selectedBankProfile.overnightAddress.country || 'US',
                      ]
                        .filter(Boolean)
                        .join(', ')}
                    </Typography>
                  </Box>
                )}
                <Button
                  startIcon={<AddIcon />}
                  onClick={() => setBankProfileDialogOpen(true)}
                  variant='outlined'
                  size='small'
                  sx={{ mb: 2 }}
                >
                  Create new bank profile
                </Button>
              </>
            )}

            <FormControlLabel
              control={
                <Checkbox
                  checked={Boolean(financeInfo.transleaseLease)}
                  onChange={evt => handleChangeFinance('transleaseLease', evt.target.checked)}
                  disabled={financeInfo.paymentType === 'cash'}
                />
              }
              label='TransLease - Lease'
              disabled={financeInfo.paymentType === 'cash'}
              sx={{ mb: 2, display: 'block' }}
            />

            {/* Sale Price Calculation Options */}
            <Typography variant='subtitle1' sx={{ mt: 3, mb: 1 }}>
              Total Sale Price Options:
            </Typography>

            <Box sx={{ mb: 2 }}>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Switch
                      checked={saleCalculation.includeWarranty}
                      onChange={e => handleSaleCalculationChange('includeWarranty', e.target.checked)}
                      disabled={!warrantyPurchased || !warrantyInfo.customerCost}
                    />
                  }
                  label={`Include Warranty (${warrantyPurchased && warrantyInfo.customerCost ? `$${warrantyInfo.customerCost}` : 'N/A'})`}
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={saleCalculation.includeShipping}
                      onChange={e => handleSaleCalculationChange('includeShipping', e.target.checked)}
                      disabled={!shippingInfo.shipped || !shippingInfo.customerShippingCost}
                    />
                  }
                  label={`Include Shipping (${shippingInfo.shipped && shippingInfo.customerShippingCost ? `$${shippingInfo.customerShippingCost}` : 'N/A'})`}
                />

                <FormControlLabel
                  control={
                    <Switch
                      checked={saleCalculation.includeTaxes}
                      onChange={e => handleSaleCalculationChange('includeTaxes', e.target.checked)}
                    />
                  }
                  label='Include Taxes'
                />
              </FormGroup>

              {saleCalculation.includeTaxes && (
                <TextField
                  label='Tax Rate (%)'
                  type='number'
                  value={saleCalculation.taxRate}
                  onChange={e => handleSaleCalculationChange('taxRate', Number(e.target.value))}
                  InputProps={{
                    endAdornment: <InputAdornment position='end'>%</InputAdornment>,
                  }}
                  sx={{ mt: 1, width: '200px' }}
                  inputProps={{ min: 0, max: 100, step: 0.01 }}
                />
              )}
            </Box>

            {/* Price Information Box */}
            <Box sx={{ display: 'flex', gap: 2 }}>
              {/* Vehicle List Price */}
              {price && (
                <Box sx={{ flex: 1, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                  <Typography variant='subtitle1'>Vehicle List Price:</Typography>
                  <Typography variant='h6'>${price.toLocaleString()}</Typography>
                </Box>
              )}

              {/* Total Sale Price */}
              <Box sx={{ flex: 1, p: 2, bgcolor: 'primary.dark', color: 'primary.contrastText', borderRadius: 1 }}>
                <Typography variant='subtitle1'>Total Sale Price:</Typography>
                <Typography variant='h6'>
                  $
                  {calculateTotalSalePrice.toLocaleString(undefined, {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </Typography>
                <Typography variant='caption'>
                  {saleCalculation.includeWarranty && warrantyPurchased && 'Including warranty • '}
                  {saleCalculation.includeShipping && shippingInfo.shipped && 'Including shipping • '}
                  {saleCalculation.includeTaxes && `Including ${saleCalculation.taxRate}% tax`}
                </Typography>
              </Box>
            </Box>
          </>
        )}

        {activeStep === STEP_FUNDED && !isPendingOnly && (
          <>
            <Typography variant='h6' sx={{ mb: 2 }}>
              Is this deal funded?
            </Typography>
            <FormControlLabel
              control={
                <Switch checked={funded} onChange={evt => setFunded(evt.target.checked)} />
              }
              label='Deal is funded'
            />
            {initialData?.fundedDate && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                <Typography variant='subtitle2' color='text.secondary' gutterBottom>
                  Funded date on file
                </Typography>
                <Typography variant='body1' fontWeight='medium'>
                  {new Date(initialData.fundedDate).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'short',
                    day: 'numeric',
                  })}
                </Typography>
              </Box>
            )}
            {funded && (
              <Box sx={{ mt: 2, p: 2, bgcolor: 'action.hover', borderRadius: 1 }}>
                <Typography variant='body2' color='text.secondary'>
                  When marked as funded, the vehicle's seller asking price will be added to the checkbook balance.
                </Typography>
              </Box>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ p: { xs: 1, sm: 2 }, justifyContent: 'space-between' }}>
        <Box>
          <Button onClick={handleClose}>Cancel</Button>
        </Box>
        <Box>
          {activeStep > STEP_CLIENT && (
            <Button onClick={handleBack} sx={{ mr: 1 }}>
              Back
            </Button>
          )}
          {activeStep < finalStepIndex ? (
            <Button onClick={handleNext} variant='contained' color='primary' disabled={isNextDisabled()}>
              Next
            </Button>
          ) : (
            <Button
              onClick={handleSubmit}
              variant='contained'
              color='primary'
              disabled={isSubmitting || (!location && !initialData?.soldLocation)}
              startIcon={isSubmitting ? <CircularProgress size={16} color='inherit' /> : undefined}
            >
              Mark as {actionText}
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
    <BankProfileDialog
      open={bankProfileDialogOpen}
      onClose={() => setBankProfileDialogOpen(false)}
      onSubmit={handleSubmitBankProfile}
    />
    </>
  )
}

export default SoldDialog
